import { CategoryComparisonCard } from "#components/dashboard/dashboard-cards/category-comparison-card"
import { ExpenseSourcesCard } from "#components/dashboard/dashboard-cards/expense-sources-card"
import { GlobalSummaryCard } from "#components/dashboard/dashboard-cards/global-summary-card"
import { MonthlyBalanceCard } from "#components/dashboard/dashboard-cards/monthly-balance-card"
import { MonthlyExpensesCard } from "#components/dashboard/dashboard-cards/monthly-expenses-card"
import { PartnerContributionCard } from "#components/dashboard/dashboard-cards/partner-contribution-card"
import { TopExpensesCard } from "#components/dashboard/dashboard-cards/top-expenses-card"
import SankeyDiagram from "#components/dashboard/sankey-diagram/sankey-diagram"
import { ErrorAlert } from "#components/ErrorAlert"
import { useDashboardAnalytics } from "#hooks/use-dashboard-analytics"
import { useI18n } from "#hooks/use-i18n"
import { useIsDashboardEmpty } from "#hooks/use-onboarding-setup"
import { useUpdateTourStatus } from "#hooks/use-tour-status"
import { useUrlFilters } from "#hooks/use-url-filters"
import { slideUp, staggerContainer } from "#lib/animations"
import { deserializeDashboardFilters, serializeDashboardFilters } from "#lib/filter-serialization"
import { queryKeys } from "#lib/query-keys"
import type { DashboardFilters as DashboardFiltersType } from "@home-finance-manager/shared/types/dashboard"
import { useTour } from "@reactour/tour"
import { useQueryClient } from "@tanstack/react-query"
import { motion } from "framer-motion"
import { HelpCircle, Rocket } from "lucide-react"
import React, { useCallback, useMemo, useState } from "react"
import { DashboardHeaderActions } from "../components/dashboard/DashboardHeaderActions"
import { OnboardingStep } from "../components/onboarding/OnboardingStep"
import { PageHeader } from "../components/page-header"
import { TourProviderWrapper } from "../components/tour/TourProviderWrapper"
import { Button } from "../components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card"
import { Body } from "../components/ui/typography"
import { getDashboardTourSteps } from "../tours/dashboard-tour"

const DashboardContent: React.FC<{
  onResetTour?: () => void
}> = ({ onResetTour }) => {
  const [isFiltersOpen, setIsFiltersOpen] = useState(false)
  const { setIsOpen } = useTour()
  const { t } = useI18n()

  const handleTourButtonClick = async () => {
    if (onResetTour) {
      await onResetTour()
      await new Promise((resolve) => setTimeout(resolve, 200))
    }
    setIsOpen(true)
  }

  // Use URL params for filters
  const [filters, setFilters] = useUrlFilters<DashboardFiltersType>(
    {},
    serializeDashboardFilters,
    deserializeDashboardFilters,
    ["search", "categoryIds", "partnerIds", "startDate", "endDate", "includeVirtualExpenses"]
  )

  const filtersWithDefaultDateRange = useMemo(() => {
    if (filters.startDate && !filters.endDate) {
      return filters
    }

    if (filters.startDate && filters.endDate) {
      return filters
    }

    const now = new Date()
    const currentMonthStart = new Date(now.getFullYear(), now.getMonth(), 1)
    const currentMonthEnd = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59, 999)

    return {
      ...filters,
      startDate: filters.startDate || currentMonthStart.toISOString(),
      endDate: filters.endDate || currentMonthEnd.toISOString(),
    }
  }, [filters])

  const queryClient = useQueryClient()
  const {
    data: analytics,
    isLoading,
    error,
    refetch,
  } = useDashboardAnalytics(filtersWithDefaultDateRange)

  // Get the first (and typically only) month's data from the date range
  // The backend will return data for the selected date range, which may span multiple months
  // We'll use the most recent month's data for the sankey diagram
  const selectedSankeyData = useMemo(() => {
    if (!analytics?.sankeyDataByMonth || analytics.sankeyDataByMonth.length === 0) {
      return null
    }
    // Get the most recent month (last in the array, as they're sorted chronologically)
    return analytics.sankeyDataByMonth[analytics.sankeyDataByMonth.length - 1] || null
  }, [analytics])

  const handleRefetch = useCallback(() => {
    refetch()
  }, [refetch])

  const handleFiltersChange = useCallback(
    (newFilters: DashboardFiltersType) => {
      setFilters(newFilters)
      // If filters are being reset (empty), invalidate queries to force refetch
      if (!newFilters || Object.keys(newFilters).length === 0) {
        queryClient.invalidateQueries({ queryKey: queryKeys.dashboard.all })
      }
    },
    [setFilters, queryClient]
  )

  if (error) {
    const errorMessage = error instanceof Error ? error.message : "Unknown error"
    return (
      <div className="p-4 sm:p-6 flex items-center justify-center overflow-x-hidden">
        <div className="w-full max-w-md">
          <ErrorAlert error={errorMessage} title={t("pages.dashboard.errorLoading")} />
        </div>
      </div>
    )
  }

  return (
    <div className="p-4 sm:p-6 flex flex-col">
      <div data-tourid="dashboard-header">
        <PageHeader
          title={t("pages.dashboard.title")}
          subtitle={t("pages.dashboard.subtitle")}
          actions={
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="icon"
                onClick={handleTourButtonClick}
                title={t("pages.dashboard.startTour")}
                className="shadow-sm dark:shadow-none dark:border-border/50 hover:shadow-md dark:hover:shadow-none transition-shadow"
              >
                <HelpCircle className="w-5 h-5" />
              </Button>
              <DashboardHeaderActions
                isLoading={isLoading}
                onRefetch={handleRefetch}
                filters={filtersWithDefaultDateRange}
                onFiltersChange={handleFiltersChange}
                isFiltersOpen={isFiltersOpen}
                onFiltersOpenChange={setIsFiltersOpen}
              />
            </div>
          }
        />
      </div>

      {/* Main Content: Sankey Diagram */}
      <div className="mb-6 sm:mb-8">
        {/* Global Financial Summary */}
        <div data-tourid="global-summary">
          <GlobalSummaryCard analytics={analytics} />
        </div>
        <motion.div initial="hidden" animate="visible" variants={slideUp}>
          <Card className="w-full flex flex-col min-h-[400px] sm:min-h-[600px] shadow-sm dark:shadow-none dark:border-border/50">
            <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 pb-4 border-b border-border/50">
              <div className="flex flex-col gap-1.5">
                <CardTitle className="text-xl sm:text-2xl">
                  {t("pages.dashboard.sankey.title")}
                </CardTitle>
                <CardDescription className="text-sm sm:text-base">
                  {t("pages.dashboard.sankey.description")}
                </CardDescription>
              </div>
            </CardHeader>
            <CardContent className="flex-1 flex flex-col p-6">
              {isLoading ? (
                <div className="w-full h-full flex items-center justify-center">
                  <div className="text-center">
                    <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                    <Body className="text-muted-foreground">
                      {t("pages.dashboard.loadingAnalytics")}
                    </Body>
                  </div>
                </div>
              ) : selectedSankeyData ? (
                <div className="flex-1 min-h-0" data-tourid="sankey-diagram">
                  <SankeyDiagram
                    nodes={selectedSankeyData.rechartsData.nodes}
                    links={selectedSankeyData.rechartsData.links}
                  />
                </div>
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <Body className="text-muted-foreground">
                    {t("pages.dashboard.noDataAvailable")}
                  </Body>
                </div>
              )}
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Bottom Analytics Cards */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={staggerContainer}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6 mb-6"
        style={{ gridAutoRows: "1fr" }}
      >
        <div data-tourid="monthly-expenses-card">
          <MonthlyExpensesCard analytics={analytics} />
        </div>
        <div data-tourid="expense-sources-card">
          <ExpenseSourcesCard analytics={analytics} />
        </div>
        <div data-tourid="monthly-balance-card">
          <MonthlyBalanceCard analytics={analytics} />
        </div>
        <div data-tourid="partner-contribution-card">
          <PartnerContributionCard analytics={analytics} />
        </div>
        <div data-tourid="top-expenses-card">
          <TopExpensesCard analytics={analytics} />
        </div>
        <div data-tourid="category-comparison-card">
          <CategoryComparisonCard analytics={analytics} />
        </div>
      </motion.div>
    </div>
  )
}

const Dashboard: React.FC = () => {
  const { needsOnboarding, isLoading: isEmptyLoading } = useIsDashboardEmpty()
  const updateTourStatus = useUpdateTourStatus()
  const { t } = useI18n()

  const onboardingStepContent = React.useMemo(
    () => (
      <div>
        <h3 className="font-semibold text-base mb-2 flex items-center gap-2">
          <Rocket className="w-5 h-5 text-primary" />
          {t("pages.dashboard.empty.title")}
        </h3>
        <p className="text-sm leading-relaxed mb-4">{t("pages.dashboard.empty.description")}</p>
        <OnboardingStep onComplete={() => {}} />
      </div>
    ),
    [t]
  )

  const allTourSteps = React.useMemo(() => {
    const translatedSteps = getDashboardTourSteps(t)
    if (needsOnboarding && !isEmptyLoading) {
      return [
        {
          selector: '[data-tourid="dashboard-header"]',
          content: onboardingStepContent,
          position: "center" as const,
          disableMask: false,
        },
        ...translatedSteps,
      ]
    }
    return translatedSteps
  }, [needsOnboarding, isEmptyLoading, onboardingStepContent, t])

  const handleResetTour = async () => {
    await updateTourStatus.mutateAsync({
      tourStartedAt: null,
      tourCompletedAt: null,
      tourStepIndex: null,
    })
  }

  return (
    <TourProviderWrapper
      steps={allTourSteps}
      autoStart={true}
      needsOnboarding={needsOnboarding && !isEmptyLoading}
    >
      <DashboardWithOnboarding onResetTour={handleResetTour} />
    </TourProviderWrapper>
  )
}

const DashboardWithOnboarding: React.FC<{
  onResetTour?: () => void
}> = ({ onResetTour }) => {
  return <DashboardContent onResetTour={onResetTour} />
}

export default Dashboard
